import {
    AIModelType,
    deleteFeedbackById,
    getAllFeedback,
    IFeedback,
} from "@/api";
import { AdminPageHeader } from "@/components/admin";
import { StatusChip } from "@/components/admin/chips/StatusChip";
import { UserChip } from "@/components/shared/chips/UserChip";
import { DateFilterInput } from "@/components/shared/table/DateFilterInput";
import { Table } from "@/components/shared/table/Table";
import { useFilterModel } from "@/hooks/useFilterModel";
import { formatDate, handleRowSelection } from "@/utils";
import { Tooltip } from "@mui/material";
import {
    GridColDef,
    GridRenderCellParams,
    GridRowParams,
    GridRowSelectionModel,
} from "@mui/x-data-grid-pro";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { DeleteFeedbackButton } from "./DeleteFeedbackButton";
import { EditFeedbackStatus } from "./EditFeedbackStatus";
import { ScopeClauseLink } from "./ScopeClauseLink";

import { Button } from "@/components/shared/buttons/Button";
import { DestructiveDialog } from "@/components/shared/dialogs/destructive-dialog/DestructiveDialog";
import { DestructiveDialogProps } from "@/components/shared/dialogs/destructive-dialog/DestructiveDialog.types";
import { useQueryClient } from "@tanstack/react-query";
import { useDialogs } from "@toolpad/core/useDialogs";
import FeedbackExportButton from "./FeedbackExportButton";

const STATUS_OPTIONS = ["Unresolved", "Under Review", "Resolved"];

const columns: GridColDef<IFeedback>[] = [
    {
        field: "clause",
        headerName: "Clause",
        display: "flex",
        valueGetter: (_, row) => {
            return row.scope_clause.clause.title;
        },
    },
    {
        field: "status",
        type: "singleSelect",
        headerName: "Status",
        display: "flex",
        valueOptions: STATUS_OPTIONS,
        renderCell: ({ row }: GridRenderCellParams<IFeedback>) => (
            <StatusChip status={row.status} />
        ),
        renderEditCell: ({ row }: GridRenderCellParams<IFeedback>) => {
            return <EditFeedbackStatus id={row.id} feedback={row} />;
        },
        editable: true,
    },
    {
        field: "organization",
        headerName: "Organization",
        display: "flex",
        valueGetter: (_, row) => {
            return row.organization.name;
        },
    },
    {
        field: "scope",
        headerName: "Document",
        display: "flex",
        renderCell: ({ row }: GridRenderCellParams<IFeedback>) => {
            return <ScopeClauseLink scopeClause={row.scope_clause} />;
        },
    },
    {
        field: "feedback_type",
        headerName: "Type",
        display: "flex",
    },
    {
        field: "additional_comments",
        headerName: "Additional Comments",
        display: "flex",
    },
    {
        field: "model_type",
        headerName: "Model",
        display: "flex",
        type: "singleSelect",
        valueOptions: Object.entries(AIModelType).map(([value, label]) => ({
            label,
            value,
        })),
        valueGetter: (_, { scope_clause }) => {
            return scope_clause.scope.model_type;
        },
        valueFormatter: (value) => {
            return AIModelType[value];
        },
    },
    {
        field: "user",
        headerName: "User",
        display: "flex",
        renderCell: ({ row }: GridRenderCellParams<IFeedback>) => (
            <Tooltip
                title={`${row.user.first_name} ${row.user.last_name}`}
                key={row.user.email}
            >
                <div>
                    <UserChip user={row.user} />
                </div>
            </Tooltip>
        ),
    },
    {
        field: "created_at",
        headerName: "Date",
        type: "date",
        display: "flex",
        filterOperators: [
            {
                label: "Date is",
                value: "dateIs",
                getApplyFilterFn: (filterItem) => {
                    if (!filterItem.value) {
                        return null;
                    }
                    return ({ value }) => {
                        return value === filterItem.value;
                    };
                },
                InputComponent: DateFilterInput,
            },
        ],
        valueFormatter: (value) => {
            return formatDate(value);
        },
    },
    {
        field: "actions",
        headerName: "Actions",
        display: "flex",
        type: "actions",
        getActions: ({ row }: GridRowParams<IFeedback>) => [
            <DeleteFeedbackButton feedbackId={row.id} />,
        ],
    },
];

export const FeedbackTable = () => {
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    const handleRowSelectionModelChange = (
        rowSelectionModel: GridRowSelectionModel
    ) => {
        setSelectedIds(handleRowSelection(rowSelectionModel, rows));
    };

    const dialogs = useDialogs();
    const queryClient = useQueryClient();

    const dialogProps: DestructiveDialogProps = {
        itemIds: selectedIds,
        subject: "feedback",
        mutationFn: deleteFeedbackById,
        onSucceeded: () => {
            queryClient.invalidateQueries({
                queryKey: ["admin-feedback"],
            });
        },
    };

    const handleClick = async () => {
        await dialogs.open(DestructiveDialog, dialogProps);
    };

    const { filterModel, handleFilterModelChange } = useFilterModel();

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });

    const [rows, setRows] = useState<IFeedback[]>([]);

    const autoSizeOptions = {
        columns: [
            "clause",
            "status",
            "organization",
            "scope",
            "feedback_type",
            "additional_comments",
            "model_type",
            "user",
            "created_at",
        ],
        includeHeaders: true,
        includeOutliers: true,
        expand: true,
    };

    const { data, isLoading } = useQuery({
        queryKey: [
            "admin-feedback",
            paginationModel.page,
            paginationModel.pageSize,
            filterModel,
        ],
        queryFn: () =>
            getAllFeedback(
                paginationModel.page + 1,
                paginationModel.pageSize,
                filterModel
            ),
    });

    useEffect(() => {
        if (data) {
            setRows(data.items);
        }
    }, [data]);

    const rowCountRef = useRef(data?.total || 0);

    const rowCount = useMemo(() => {
        if (data?.total !== undefined) {
            rowCountRef.current = data?.total;
        }
        return rowCountRef.current;
    }, [data?.total]);

    return (
        <>
            <AdminPageHeader
                title="Feedback"
                subtitle="Review user feedback and conversation ratings"
            >
                <FeedbackExportButton />
                {selectedIds.length > 0 && (
                    <Button
                        variant="contained"
                        color="tertiary"
                        disableElevation
                        sx={{ borderRadius: 22 }}
                        onClick={handleClick}
                    >
                        Delete Selected
                    </Button>
                )}
            </AdminPageHeader>

            <div className="mt-6">
                <Table
                    loading={isLoading}
                    columns={columns}
                    rows={rows}
                    rowCount={rowCount}
                    checkboxSelection
                    onRowSelectionModelChange={handleRowSelectionModelChange}
                    disableRowSelectionOnClick
                    disableColumnResize
                    autoSize
                    autosizeOptions={autoSizeOptions}
                    filterMode="server"
                    onFilterModelChange={handleFilterModelChange}
                    paginationMode="server"
                    paginationModel={paginationModel}
                    sortingMode="server"
                    onSortModelChange={handleFilterModelChange}
                    onPaginationModelChange={setPaginationModel}
                />
            </div>
        </>
    );
};
